import { useState, useEffect } from "react";
import api from "../api";

const EMPTY_ARRAY = [];

/**
 * Fetch the cases belonging to a patient for the report case-selector.
 * Returns select-ready options labelled "CASE_NO(Program Name)".
 *
 * IMPORTANT: `ready` and `caseOptions` are DERIVED from whether the fetched data
 * belongs to the *current* patientId. This avoids a race where, right after the
 * patient changes, a consumer effect could still read the previous patient's
 * ready/options (stale async state) and fire a premature report request.
 *
 * @param {number|string} patientId
 * @returns {{ caseOptions: {value:number,label:string}[], loading: boolean, ready: boolean }}
 *   ready === true only once the case fetch for THIS patient has settled.
 */
export default function usePatientCases(patientId) {
  // data.patientId records which patient the caseOptions actually belong to.
  const [data, setData] = useState({ patientId: null, caseOptions: EMPTY_ARRAY });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setData((prev) => (prev.patientId === null && prev.caseOptions === EMPTY_ARRAY ? prev : { patientId: null, caseOptions: EMPTY_ARRAY }));
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .get(`/cases/patients/${patientId}/cases`)
      .then((res) => {
        const cases = res.data?.data || [];
        const opts = cases.map((c) => ({
          value: c.id,
          label: `${c.case_number || `Case ${c.id}`}(${c.program?.name || "N/A"})`,
        }));
        if (active) setData({ patientId, caseOptions: opts });
      })
      .catch(() => {
        // 404 = patient has no cases; still mark it as loaded for THIS patient
        if (active) setData({ patientId, caseOptions: EMPTY_ARRAY });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [patientId]);

  // Only expose the options / readiness when they match the patient asked for now.
  const matched = !!patientId && data.patientId === patientId;
  return {
    caseOptions: matched ? data.caseOptions : EMPTY_ARRAY,
    loading,
    ready: matched,
  };
}
